<template>
	<button
		v-show="showGhost"
		:id="ghostId"
		ref="ghost"
		type="button"
		@click="handleMouseUp($event)"
	/>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import {
	type ChartInteractionPayload,
	type ResourcePlanningItem,
} from '../../types';
import { CHART_ITEM_TYPE, CHART_ROW_TYPE } from '../../constants';
import { type ResourcePlanningGanttConfig } from '../ResourcePlanningGantt/ResourcePlanningGanttConfig';
import { isWithinInterval } from 'date-fns';

interface ResourcePlanningChartInteractionProps {
	container: HTMLElement;
	config: ResourcePlanningGanttConfig;
}

const props = defineProps<ResourcePlanningChartInteractionProps>();
const emit = defineEmits<{
	(e: 'click', payload: ChartInteractionPayload): void;
}>();

const GANTT_ROW_CLASS = '.gantt_task_row, .gantt_task_line' as const;
const HEATMAP_ELEMENT_CLASS = '.resource-planning__heatmap-element' as const;

const ghostId = 'resource-planning-placeholder-ghost';
const ghost = ref<HTMLElement>();
const mouseDownCoordinates = ref<number[]>([]);
const showGhost = ref(false);
const targetResource = ref<ResourcePlanningItem | null>(null);
const hoverDate = ref<Date | null>(null);

const hideGhost = () => {
	targetResource.value = null;
	hoverDate.value = null;
	showGhost.value = false;
};

const updateGhostPosition = (e: MouseEvent) => {
	const targetRow = (e.target as HTMLElement | null)?.closest(
		GANTT_ROW_CLASS
	) as HTMLElement | null;

	if (!targetRow || !ghost.value) {
		return;
	}

	const leftPosition = e.clientX - ghost.value.clientWidth / 2;
	const topPosition =
		targetRow.getBoundingClientRect().top +
		(targetRow.clientHeight - ghost.value.clientHeight) / 2;

	ghost.value.style.left = `${leftPosition}px`;
	ghost.value.style.top = `${topPosition}px`;
};

const handleMouseDown = (e: MouseEvent) => {
	mouseDownCoordinates.value = [e.clientX, e.clientY];
};

const handleMouseMove = (e: MouseEvent) => {
	const target = e.target as HTMLElement | null;
	const targetRow = (target?.closest(GANTT_ROW_CLASS) ||
		target?.closest(HEATMAP_ELEMENT_CLASS)) as HTMLElement | null;

	const resourceId = targetRow?.dataset.taskId ?? '';
	const resource = props.config.gantt.getTask(resourceId);
	const cursorOutsideChart =
		window.outerWidth - e.clientX <
		props.container.getBoundingClientRect().left;

	if (cursorOutsideChart) {
		hideGhost();
		return;
	}

	const dataAreaElem = props.container.querySelector(
		'.gantt_data_area'
	) as HTMLElement | null;
	const leftPositionOnChart = Math.abs(
		e.clientX -
			(dataAreaElem?.getBoundingClientRect().left ?? 0) +
			window.scrollX
	);

	if (resource) {
		hoverDate.value = props.config.gantt.dateFromPos(leftPositionOnChart);
		targetResource.value = resource;

		if (resource?.row_type === CHART_ROW_TYPE.HEATMAP) {
			targetResource.value = resource;
			showGhost.value = false;

			return;
		} else if (resource.row_type === CHART_ROW_TYPE.BOOKINGS) {
			let existingBookingOnHoverDate = false;

			props.config.gantt.eachTask((item: ResourcePlanningItem) => {
				if (existingBookingOnHoverDate) {
					return;
				}

				if (item.item_type === CHART_ITEM_TYPE.BOOKING) {
					existingBookingOnHoverDate = isWithinInterval(
						hoverDate.value ?? 0,
						{
							start: item.start_date ?? new Date(),
							end: item.end_date ?? new Date(),
						}
					);
				}
			}, resource.id);

			if (
				!props.config.canModifyProject(resource) ||
				existingBookingOnHoverDate
			) {
				hideGhost();

				return;
			}
		} else if (resource.item_type === CHART_ITEM_TYPE.BOOKING) {
			const project = props.config.gantt.getTask(resource.parent ?? '');
			showGhost.value = false;

			if (!props.config.canModifyProject(project)) {
				hideGhost();
			}

			return;
		}
	} else {
		hideGhost();

		return;
	}

	showGhost.value = true;
	updateGhostPosition(e);
};

const handleMouseLeave = () => {
	hideGhost();
};

const cancelAddBooking = (e: MouseEvent) => {
	const mouseMoveThreshold = 30;
	const [clientX, clientY] = mouseDownCoordinates.value;

	if (
		Math.abs(clientX - e.clientX) > mouseMoveThreshold ||
		Math.abs(clientY - e.clientY) > mouseMoveThreshold
	) {
		return true;
	}

	return false;
};

const handleMouseUp = (e: MouseEvent) => {
	if (cancelAddBooking(e)) {
		return;
	}

	const target = document.createElement('div');
	target.style.position = 'fixed';
	target.style.left = `${e.clientX}px`;
	target.style.top = `${e.clientY}px`;

	document.body.append(target);

	const resource = targetResource.value;

	const payload = {
		target,
		resource,
		date: hoverDate.value,
	};

	emit('click', payload);
	hideGhost();
};

onMounted(() => {
	props.container.addEventListener('mousedown', handleMouseDown);
	props.container.addEventListener('mousemove', handleMouseMove);
	props.container.addEventListener('mouseleave', handleMouseLeave);
	props.container.addEventListener('mouseup', handleMouseUp);
});
</script>

<style lang="scss" scoped>
#resource-planning-placeholder-ghost {
	position: fixed;
	width: 2rem;
	height: 4rem;
	background: $blue-10;
	border: 1px solid $blue-40;
	border-radius: 0.5rem;
	opacity: 0.5;
	cursor: pointer;
	pointer-events: none;
}
</style>
